import { useState, useMemo, useCallback } from 'react';
import { Search, Download, Maximize, FileText, FileSpreadsheet, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';

import { OrgChartHeader } from './OrgChartHeader';
import { WorkforceKpiCards } from './WorkforceKpiCards';
import { DepartmentFilterRow } from './DepartmentFilterRow';
import { ChartToolbar } from './ChartToolbar';
import { HierarchyCanvas } from './HierarchyCanvas';

import {
  flattenTree,
  cloneNode,
  isUzefDurrani,
  isActualDeptAdmin,
  isActualLead,
  normalizeDept,
  getDeptConfig,
  buildDepartmentHierarchy,
  filterHierarchyTree,
  ROLE_LABELS,
  ROLE_COLORS,
  ROLE_RANK,
  type OrgNode,
  type OrgChartResponse,
} from './OrgChart';

// ─── Types ────────────────────────────────────────────────────────────────────

type ViewMode = 'hierarchy' | 'department';

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useOrgChart() {
  return useQuery({
    queryKey: ['org-chart'],
    queryFn: async () => {
      const { data } = await api.get<OrgChartResponse>('/api/org-chart');
      return data;
    },
  });
}

function useReassignManager() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, managerId }: { userId: string; managerId: string | null }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/users/${userId}/manager`, {
        manager_id: managerId,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-chart'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

function nodeMatchesSearch(node: OrgNode, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    node.display_name.toLowerCase().includes(q) ||
    (node.email || '').toLowerCase().includes(q) ||
    (node.department || '').toLowerCase().includes(q) ||
    (node.position || '').toLowerCase().includes(q) ||
    (node.role || '').toLowerCase().includes(q)
  );
}

function treeContainsMatch(node: OrgNode, query: string): boolean {
  if (nodeMatchesSearch(node, query)) return true;
  return node.children.some((child) => treeContainsMatch(child, query));
}

// ─── Main OrgChart Component ──────────────────────────────────────────────────

export function OrgChart() {
  const { data, isLoading, isError } = useOrgChart();
  const isAdmin = data?.canManage === true;
  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState<string>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('hierarchy');
  const [zoomLevel, setZoomLevel] = useState<number>(100);
  const [centerTrigger, setCenterTrigger] = useState(0);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [reassignTarget, setReassignTarget] = useState<OrgNode | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleCollapse = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allUsers = useMemo(() => {
    if (!data) return [];
    return flattenTree(data.roots);
  }, [data]);

  // Unique list of departments with member counts
  const departmentStats = useMemo(() => {
    const map = new Map<string, { key: string; label: string; count: number; color: string; bgColor: string; borderColor: string; icon: React.ReactNode }>();
    allUsers.forEach((u: OrgNode) => {
      const key = normalizeDept(u.department);
      const conf = getDeptConfig(u.department);
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: conf.label,
          count: 0,
          color: conf.color,
          bgColor: conf.bgColor,
          borderColor: conf.borderColor,
          icon: conf.icon,
        });
      }
      map.get(key)!.count += 1;
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [allUsers]);

  // Derived org summary stats
  const orgStats = useMemo(() => {
    const departments = new Set<string>();
    let managers = 0;
    let leadership = 0;
    for (const u of allUsers) {
      if (u.department && normalizeDept(u.department) !== 'superadmin') {
        departments.add(normalizeDept(u.department));
      }
      if (u.children.length > 0) managers++;
      if (isUzefDurrani(u) || isActualDeptAdmin(u)) {
        leadership++;
      }
    }
    return { departments: departments.size, managers, leadership };
  }, [allUsers]);

  // Construct hierarchy tree
  const hierarchyRoots = useMemo(() => {
    if (!data) return [];
    return buildDepartmentHierarchy(allUsers);
  }, [data, allUsers]);

  // Filter roots by department if a filter is active
  const filteredRoots = useMemo(() => {
    return filterHierarchyTree(hierarchyRoots, selectedDept);
  }, [hierarchyRoots, selectedDept]);

  // Expand / Collapse all handlers
  const handleExpandAll = () => setCollapsed(new Set());
  const handleCollapseAll = () => {
    const allIds = new Set<string>();
    function collect(nodes: OrgNode[]) {
      for (const n of nodes) {
        if (n.children && n.children.length > 0) {
          allIds.add(n.id);
          collect(n.children);
        }
      }
    }
    collect(filteredRoots);
    setCollapsed(allIds);
  };

  // Zoom controls
  const handleZoomIn = () => setZoomLevel((z) => Math.min(150, z + 15));
  const handleZoomOut = () => setZoomLevel((z) => Math.max(50, z - 15));
  const handleZoomReset = () => {
    setZoomLevel(100);
    setCenterTrigger((c) => c + 1);
  };
  const handleCenter = () => {
    setCenterTrigger((c) => c + 1);
  };
  const handleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  // Export functions
  const handleExportPdf = useCallback(() => {
    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      // Title
      doc.setFontSize(18);
      doc.setTextColor(17, 24, 63); // #11183F
      doc.text('Organization Chart', 14, 20);

      // Subtitle
      doc.setFontSize(10);
      doc.setTextColor(116, 124, 156); // #747C9C
      doc.text(`Generated on ${new Date().toLocaleDateString()} - ${data?.nodeCount ?? allUsers.length} Team Members`, 14, 27);

      // Prepare table data
      const tableData = allUsers.map((user) => [
        user.display_name,
        user.position || ROLE_LABELS[user.role.toLowerCase()] || user.role,
        getDeptConfig(user.department).label,
        user.email || '-',
        user.manager_id ? 'Yes' : 'No',
      ]);

      // Add table
      autoTable(doc, {
        startY: 32,
        head: [['Name', 'Position', 'Department', 'Email', 'Has Manager']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [104, 37, 245], // #6825F5
          textColor: 255,
          fontSize: 8,
          fontStyle: 'bold',
        },
        bodyStyles: {
          fontSize: 7,
          textColor: [17, 24, 63],
        },
        alternateRowStyles: {
          fillColor: [242, 242, 250], // #F2F2FA
        },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 35 },
          2: { cellWidth: 35 },
          3: { cellWidth: 50 },
          4: { cellWidth: 20, halign: 'center' },
        },
        margin: { left: 14, right: 14 },
      });

      const fileName = `organization-chart-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  }, [allUsers, data?.nodeCount]);

  const handleExportExcel = useCallback(() => {
    try {
      // Prepare data for Excel
      const excelData = allUsers.map((user) => ({
        Name: user.display_name,
        Position: user.position || ROLE_LABELS[user.role.toLowerCase()] || user.role,
        Department: getDeptConfig(user.department).label,
        Email: user.email || '-',
        'Has Manager': user.manager_id ? 'Yes' : 'No',
        Role: user.role,
        EmployeeId: user.id,
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 25 }, // Name
        { wch: 30 }, // Position
        { wch: 25 }, // Department
        { wch: 35 }, // Email
        { wch: 12 }, // Has Manager
        { wch: 15 }, // Role
        { wch: 15 }, // EmployeeId
      ];
      worksheet['!cols'] = colWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Organization Chart');

      const fileName = `organization-chart-${new Date().toISOString().split('T')[0]}.xlsx`;
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
      saveAs(blob, fileName);

      toast.success('Excel file exported successfully');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Failed to export Excel file');
    }
  }, [allUsers]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isError || !data) {
    return <EmptyState title="Failed to load org chart" description="Check your connection and try again." />;
  }

  if (data.roots.length === 0) {
    return <EmptyState title="No employees found" description="Add users to see the organization chart." />;
  }

  return (
    <div
      className={
        isFullscreen
          ? 'fixed inset-0 z-50 flex flex-col gap-3 bg-[#F7F7FC] p-4 overflow-hidden'
          : 'flex flex-col gap-3 bg-[#F7F7FC] h-[calc(100vh-130px)] min-h-[640px] rounded-xl border border-[#E8E8F5] shadow-sm overflow-hidden'
      }
    >
      {/* Header Bar */}
      <div className="px-6 py-3.5 bg-white border-b border-[#E8E8F5] shrink-0">
        <OrgChartHeader
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          totalMembers={data.nodeCount}
          onExportPdf={handleExportPdf}
          onExportExcel={handleExportExcel}
        />
      </div>

      {/* Summary KPI Tiles */}
      <div className="px-6 py-3 bg-white border-b border-[#E8E8F5] shrink-0">
        <WorkforceKpiCards
          totalWorkforce={data.nodeCount}
          activeDepartments={orgStats.departments}
          leadershipRoles={orgStats.leadership}
          reportingManagers={orgStats.managers}
        />
      </div>

      {/* Department Filter Bar */}
      <div className="px-6 py-2.5 bg-white border-b border-[#E8E8F5] shrink-0">
        <DepartmentFilterRow
          selectedDept={selectedDept}
          onDeptChange={setSelectedDept}
          departments={departmentStats}
          totalCount={allUsers.length}
        />
      </div>

      {/* Chart Controls Toolbar (only in Hierarchy view) */}
      {viewMode === 'hierarchy' && (
        <div className="px-6 py-2 bg-white border-b border-[#E8E8F5] shrink-0">
          <ChartToolbar
            zoomLevel={zoomLevel}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomReset={handleZoomReset}
            onCenter={handleCenter}
            onFullscreen={handleFullscreen}
            onExpandAll={handleExpandAll}
            onCollapseAll={handleCollapseAll}
            isFullscreen={isFullscreen}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 flex flex-col px-6 pb-6 overflow-hidden">
        {viewMode === 'department' ? (
          /* ─── Department Cards Matrix View ─── */
          <div className="flex-1 min-h-0 overflow-y-auto hierarchy-scrollbar rounded-xl border border-[#E8E8F5] bg-white p-6 shadow-inner">
            <DepartmentMatrixView
              allUsers={allUsers}
              selectedDept={selectedDept}
              search={search}
              isAdmin={isAdmin}
              onReassign={setReassignTarget}
            />
          </div>
        ) : (
          /* ─── Hierarchy Tree Canvas ─── */
          <HierarchyCanvas
            roots={filteredRoots}
            search={search}
            collapsed={collapsed}
            onToggle={toggleCollapse}
            isAdmin={isAdmin}
            onReassign={setReassignTarget}
            zoomLevel={zoomLevel}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onZoomReset={handleZoomReset}
            onCenter={handleCenter}
            onExpandAll={handleExpandAll}
            onCollapseAll={handleCollapseAll}
            centerTrigger={centerTrigger}
          />
        )}
      </div>

      {/* Reassign Reporting Manager Modal */}
      {reassignTarget && (
        <ReassignModal
          target={reassignTarget}
          allUsers={allUsers}
          onClose={() => setReassignTarget(null)}
        />
      )}
    </div>
  );
}

// ─── Department Matrix View ───────────────────────────────────────────────────

function DepartmentMatrixView({
  allUsers,
  selectedDept,
  search,
  isAdmin,
  onReassign,
}: {
  allUsers: OrgNode[];
  selectedDept: string;
  search: string;
  isAdmin: boolean;
  onReassign: (node: OrgNode) => void;
}) {
  const executive = useMemo(() => allUsers.find(isUzefDurrani), [allUsers]);

  // Group users by department (excluding Uzef who is top-level Executive Leadership)
  const groupedDepartments = useMemo(() => {
    const map = new Map<string, OrgNode[]>();
    allUsers.forEach((u) => {
      if (isUzefDurrani(u)) return;
      if (search && !nodeMatchesSearch(u, search)) return;
      const key = normalizeDept(u.department);
      if (selectedDept !== 'all' && key !== selectedDept) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(u);
    });

    const list = Array.from(map.entries()).map(([deptKey, members]) => {
      // Find actual specified Department Administrator or Lead
      const admin = members.find((m) => isActualDeptAdmin(m));
      const lead = !admin ? members.find((m) => isActualLead(m)) : undefined;
      const head = admin || lead || null;

      // If there is an admin/lead, the rest of members are team. If no admin/lead, ALL members are in team!
      const team = head ? members.filter((m) => m.id !== head.id) : [...members];

      team.sort((a, b) => {
        const rankA = ROLE_RANK[a.role.toLowerCase()] ?? 6;
        const rankB = ROLE_RANK[b.role.toLowerCase()] ?? 6;
        if (rankA !== rankB) return rankA - rankB;
        return a.display_name.localeCompare(b.display_name);
      });

      const conf = getDeptConfig(deptKey);

      return {
        key: deptKey,
        config: conf,
        head,
        team,
        total: members.length,
      };
    });

    list.sort((a, b) => b.total - a.total);
    return list;
  }, [allUsers, selectedDept, search]);

  return (
    <div className="h-full overflow-auto space-y-6">
      {/* Executive Leadership Spotlight (Uzef Durrani exclusively) */}
      {executive && (selectedDept === 'all' || selectedDept === 'superadmin') && (
        <div className="rounded-xl border-2 border-violet-400/50 bg-gradient-to-r from-violet-600/10 via-indigo-600/5 to-purple-600/10 p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-indigo-700 text-lg font-bold text-white shadow-md ring-2 ring-violet-300">
                <svg className="h-6 w-6 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-[#11183F]">{executive.display_name}</h3>
                  <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 px-2.5 py-0.5 text-[10px] font-bold text-white shadow-xs">
                    <svg className="h-3 w-3 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                    Executive Leadership
                  </span>
                </div>
                <p className="text-xs text-[#747C9C] font-medium">
                  {executive.position || 'Super Admin'} &bull; <span className="font-mono">{executive.email || 'uzef@durranitech.com'}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#11183F] border border-[#E8E8F5]/60 shadow-xs">
                Organization Super Administrator
              </span>
            </div>
          </div>
        </div>
      )}

      {groupedDepartments.length === 0 ? (
        <EmptyState title="No matching departments" description="Try selecting 'All Departments' or clear search." />
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {groupedDepartments.map((dept) => {
            const DeptIcon = dept.config.icon;
            return (
              <div
                key={dept.key}
                className="flex flex-col rounded-xl border border-[#E8E8F5] bg-white p-4 shadow-sm"
              >
                {/* Department Card Header */}
                <div className="mb-3 flex items-center justify-between border-b border-[#E8E8F5]/40 pb-3">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-lg shadow-xs"
                      style={{ backgroundColor: `${dept.config.color}20`, color: dept.config.color }}
                    >
                      {dept.config.icon}
                    </div>
                    <div>
                      <h2 className="text-sm font-bold text-[#11183F]">{dept.config.label}</h2>
                      <p className="text-[11px] text-[#747C9C]">{dept.total} Team Members</p>
                    </div>
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${dept.config.bgColor} border ${dept.config.borderColor}`}>
                    {dept.total}
                  </span>
                </div>

                {/* Department Administrator / Head Section */}
                <div className="mb-4">
                  <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold text-[#6825F5]">
                    <div className="flex items-center gap-1">
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Department Administrator</span>
                    </div>
                    {dept.head && (
                      <span className="text-[10px] text-[#747C9C] font-normal">Assigned</span>
                    )}
                  </div>

                  {dept.head ? (
                    <div className="relative rounded-lg border-2 border-indigo-400/40 bg-white p-3 shadow-xs">
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-xs"
                          style={{ backgroundColor: dept.config.color }}
                        >
                          {dept.head.display_name
                            .split(' ')
                            .map((w) => w[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-1">
                            <h4 className="truncate text-xs font-bold text-[#11183F]">{dept.head.display_name}</h4>
                            <span className="shrink-0 rounded bg-indigo-500/10 px-1.5 py-0.2 text-[9px] font-bold text-indigo-600">
                              {ROLE_LABELS[dept.head.role.toLowerCase()] ?? 'Admin'}
                            </span>
                          </div>
                          <p className="truncate text-[10px] text-[#747C9C]">
                            {dept.head.position || dept.head.department}
                          </p>
                        </div>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={() => onReassign(dept.head!)}
                            className="flex h-7 w-7 items-center justify-center rounded-md border border-[#E8E8F5] hover:bg-[#F2F2FA] text-[#747C9C]"
                            title="Change Manager"
                          >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-[#E8E8F5]/70 bg-[#F7F7FC] p-3 text-center">
                      <p className="text-xs font-medium text-[#747C9C]">No Administrator Assigned</p>
                      <p className="text-[10px] text-[#747C9C]/70 mt-0.5">
                        Admin is specified when creating a user for this department
                      </p>
                    </div>
                  )}
                </div>

                {/* Department Team Members List */}
                <div className="flex-1">
                  <p className="mb-2 text-[11px] font-semibold text-[#747C9C]">
                    Team Members & Roles ({dept.team.length})
                  </p>
                  {dept.team.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-[#E8E8F5] p-3 text-center text-xs text-[#747C9C]">
                      No other members in this department
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                      {dept.team.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between rounded-lg border border-[#E8E8F5]/70 bg-white p-2.5 hover:border-[#D1D1E0] transition-colors"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div
                              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                              style={{ backgroundColor: ROLE_COLORS[member.role.toLowerCase()] ?? '#6b7280' }}
                            >
                              {member.display_name
                                .split(' ')
                                .map((w) => w[0])
                                .join('')
                                .slice(0, 2)
                                .toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="truncate text-xs font-semibold text-[#11183F]">{member.display_name}</p>
                              <p className="truncate text-[10px] text-[#747C9C]">
                                {member.position || ROLE_LABELS[member.role.toLowerCase()] || member.role}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="rounded bg-[#F2F2FA] px-1.5 py-0.5 text-[9px] font-medium text-[#747C9C]">
                              {ROLE_LABELS[member.role.toLowerCase()] ?? member.role}
                            </span>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => onReassign(member)}
                                className="flex h-6 w-6 items-center justify-center rounded hover:bg-[#F2F2FA] text-[#747C9C]"
                                title="Change Manager"
                              >
                                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Reassign Manager Modal ───────────────────────────────────────────────────

function ReassignModal({
  target,
  allUsers,
  onClose,
}: {
  target: OrgNode;
  allUsers: OrgNode[];
  onClose: () => void;
}) {
  const reassign = useReassignManager();
  const [selectedManager, setSelectedManager] = useState<string>(target.manager_id ?? '');
  const isTargetUzef = isUzefDurrani(target);

  const managerOptions = useMemo(() => {
    const opts = [{ value: '', label: '\u2014 No Manager (Top / Root) \u2014' }];
    const uzef = allUsers.find(isUzefDurrani);
    if (uzef && uzef.id !== target.id) {
      opts.push({
        value: uzef.id,
        label: `\u{1F451} ${uzef.display_name} (Executive Leadership / Super Admin)`,
      });
    }
    for (const u of allUsers) {
      if (u.id === target.id) continue;
      if (isUzefDurrani(u)) continue;
      const deptConf = getDeptConfig(u.department);
      opts.push({
        value: u.id,
        label: `${u.display_name} (${ROLE_LABELS[u.role.toLowerCase()] ?? u.role} \u00B7 ${deptConf.label})`,
      });
    }
    return opts;
  }, [allUsers, target.id]);

  const handleSave = () => {
    if (isTargetUzef) return;
    reassign.mutate(
      { userId: target.id, managerId: selectedManager || null },
      {
        onSuccess: () => {
          toast.success(`Manager updated for ${target.display_name}.`);
          onClose();
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to reassign manager.')),
      }
    );
  };

  const targetDept = getDeptConfig(target.department);

  return (
    <Modal
      open
      onClose={onClose}
      title="Change Reporting Manager"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {isTargetUzef ? 'Close' : 'Cancel'}
          </Button>
          {!isTargetUzef && (
            <Button onClick={handleSave} loading={reassign.isPending}>
              Save Reporting Line
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {/* Person Info Card */}
        <div className="flex items-center gap-3 rounded-xl border border-[#E8E8F5] bg-[#F7F7FC] p-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-xs"
            style={{ backgroundColor: ROLE_COLORS[target.role.toLowerCase()] ?? '#6b7280' }}
          >
            {target.display_name
              .split(' ')
              .map((w) => w[0])
              .join('')
              .slice(0, 2)
              .toUpperCase()}
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-bold text-[#11183F] truncate">{target.display_name}</h4>
            <p className="text-xs text-[#747C9C]">
              {ROLE_LABELS[target.role.toLowerCase()] ?? target.role} \u00B7 {targetDept.label}
            </p>
            {target.position && (
              <span className="mt-1 inline-block text-[11px] font-medium text-[#6825F5]">
                Position: {target.position}
              </span>
            )}
          </div>
        </div>

        {isTargetUzef ? (
          <div className="rounded-xl border border-violet-400/50 bg-violet-500/10 p-4 text-center">
            <svg className="mx-auto h-8 w-8 text-violet-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            <h4 className="text-sm font-bold text-[#11183F]">Executive Leadership</h4>
            <p className="mt-1 text-xs text-[#747C9C]">
              Uzef Durrani is the Organization Super Administrator and Executive Leadership. They are at the summit of the hierarchy and do not report to anyone.
            </p>
          </div>
        ) : (
          <>
            <Select
              label="Direct Reporting Manager"
              name="new-manager"
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
              options={managerOptions}
            />

            <p className="text-[11px] text-[#747C9C] leading-relaxed">
              Updating the reporting line moves this team member and their direct reports under the designated manager in
              both the hierarchy tree and HR payroll/governance workflows.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}